import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { SummaryModuleDataApiModel, WVBase, WVMain } from '../wallboard-view.model';
import { SummarySettingsTransferObj, ModuleType } from '../../wallboards.model';

@Component({
    selector: 'cv-wallboard-view-summary',
    templateUrl: './wallboard-view-summary.component.html',
    styleUrls: ['./wallboard-view-summary.component.scss']
})
export class WallboardViewSummaryComponent implements OnInit, OnDestroy {
    @Input() firstSlide;

    public wvf: WVMain;
    public wvsf: WVBase;
    public summaryState: SummarySettingsTransferObj;
    public summaryData: SummaryModuleDataApiModel;
    public summaryMetricNames: string[] = [];

    public isSmallScreen: boolean = false;
    public index: number = 0;

    // Variables for pagination
    public currentPage: number = 0;
    public pageSize: number = 0;
    public totalPages: number = 0;
    public totals: any = [];
    public calculatedInterval: number = 0;
    public slideIntervalTime: number;

    private windowHeight = window.innerHeight;
    private slideHeight: number = 0;
    private tableHeight: number = 0;
    private $slideContainer: JQuery<HTMLElement>;

    private updateTimerRef: NodeJS.Timeout = null;
    private isDestroyed: boolean = false;

    private _hasBackdrop: boolean = false;
    private _hasTotalsRow: boolean = false;
    private _hasPagination: boolean = false;
    private _moduleKey: string = "";
    private _backgroundID: number = 0;
    private _wallboardID: number = 0;
    private _nextSlideID: number = 0;

    get hasBackdrop(): boolean {
        return this._hasBackdrop;
    }

    get hasTotalsRow(): boolean {
        return this._hasTotalsRow;
    }

    get hasPagination(): boolean {
        return this._hasPagination;
    }

    get moduleKey(): string {
        return this._moduleKey;
    }

    get backgroundID(): number {
        return this._backgroundID;
    }

    constructor(
        private service: WallboardViewService
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvsf = this.service.WVSummary;

        this._hasBackdrop = this.wvsf?.Slide.HasBackdrop ?? false;
        this._hasTotalsRow = this.wvsf?.Slide.HasTotalsRow ?? false;
        this._hasPagination = this.wvsf?.Slide.HasPagination ?? false;
        this._moduleKey = this.wvsf?.Slide.WallboardModuleDetails[0].WallboardModuleKey ?? "";
        this._backgroundID = this.wvsf?.Slide.BackgroundImageID ?? 0;
        this._wallboardID = this.wvsf?.Slide.WallboardID ?? 0;
        this._nextSlideID = this.wvsf?.Slide.WallboardModuleDetails[0].WallboardSlideID ?? 0;

        if (this.windowHeight <= 720)
            this.isSmallScreen = true;

        this.wvf.RunSlide = this.firstSlide;

        this.$slideContainer = angular.element(document).find('.wallboardViewSlide');
        this.$slideContainer.css("height", "100vh");

        this.tableHeight = (this.$slideContainer.find(".wallboardSummarySlideContentArea").height() - 132);

        let showTop: number = 0;

        if (this.hasPagination)
            showTop = 500;
        else if (this.isSmallScreen) {
            showTop = Math.floor(this.tableHeight / 40);
            this.pageSize = showTop;
        }
        else {
            showTop = Math.floor(this.tableHeight / 47);
            this.pageSize = showTop;
        }

        let moduleState = this.service.loadModuleState(ModuleType.Summary, this.wvf.WallboardCreatorID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.summaryState = response.sumObj;
        });

        let summaryModuleData = this.service.getSummaryModuleData(this._wallboardID, showTop, this.hasTotalsRow, this.hasPagination, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.summaryData = response;

            const verifyNextSlide = !this.firstSlide && (this._nextSlideID === this.wvf.Slides[this.wvf.NextSlide].WallboardSlideID);

            if (this.firstSlide)
                //var slideIntervalTime = this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval * 1000;
                this.slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval - .5) * 1000;
            else
                //var slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval - 1) * 1000;
                this.slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval - 1.3) * 1000;

            if (this.hasTotalsRow)
                this.totals = this.summaryData.Summary_Module_Data_List.pop();

            if (this.hasPagination) {
                // Pagination logic
                this.slideHeight = Math.floor(this.tableHeight / (this.isSmallScreen ? 40 : 47));
                this.pageSize = this.slideHeight - (this.hasTotalsRow ? 1 : 0);

                if (this.summaryData.Summary_Module_Data_List.length > this.slideHeight) {

                    if (this.summaryData.Summary_Module_Data_List.length > this.slideHeight) {
                        this.totalPages = Math.ceil(this.summaryData.Summary_Module_Data_List.length / this.pageSize);

                        this.calculatedInterval = ((this.slideIntervalTime - 1000) / this.totalPages);
                        const paginationValues = this.service.calculatePagination(this.calculatedInterval, this.slideIntervalTime, this.totalPages, verifyNextSlide);
                        this.slideIntervalTime = paginationValues[0];
                        this.calculatedInterval = paginationValues[1];
                    }
                    else
                        this.currentPage = 0;
                } else {
                    this.totalPages = 1;
                    this.currentPage = 0;
                }
            }
        });

        this.service.getSummaryMetricNames(this._wallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.summaryMetricNames = response;
        });

        Promise.all([moduleState, summaryModuleData]).then(responses => {
            if (this.firstSlide)
                this.wvf.FirstSlideReady = true;
            else
                this.wvf.NextSlideReady = true;

            this.runSlide();
        });
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null)
            clearInterval(this.updateTimerRef);

        this.isDestroyed = true;
    }

    async runSlide() {
        if (this.isDestroyed)
            return;

        if (!this.updateTimerRef) {
            const refreshInterval = this.summaryData.Has_Current_Metric
                ? WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL
                : WallboardViewService.HISTORICAL_DATA_REFRESH_INTERVAL;

            this.updateTimerRef = setInterval(() => this.updateSlideData(), refreshInterval);
        }

        if (this.hasPagination && this.wvf.RunSlide) {
            setTimeout(() => {
                if (this.calculatedInterval != null && this.totalPages > 0) {
                    for (let i = 0; i < this.totalPages; i++) {
                        setTimeout(() => {
                            this.currentPage = this.service.runPagination(this.index, this.currentPage);
                            this.index++;
                        }, i * this.calculatedInterval, true, i);
                    }
                }
            });
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    async updateSlideData() {
        let showTop = this.hasPagination ? 500 : Math.floor(this.tableHeight / 47);

        const response = await this.service.getSummaryModuleData(this._wallboardID, showTop, this.hasTotalsRow, this.hasPagination, this.moduleKey);

        if (this.isDestroyed)
            return;

        this.summaryData = response;

        if (this.hasTotalsRow)
            this.totals = response.Summary_Module_Data_List.pop();
    }

    /**
     * Returns the class names for the main content area.
     */
    getMainClasses() {
        return `summaryViewMetricArea${this.hasTotalsRow ? "Totals" : ""}${this.hasBackdrop ? " summaryViewDark" : ""}${this.isSmallScreen ? " smallScreen" : ""}`;
    }

    /**
     * Returns the number of columns that the empty space should span.
     */
    getColumnWidth() {
        // 10 - (number of metrics), with enforced minimum of 0 and maximum of 9 enforced
        return Math.max(Math.min(10 - (this.summaryMetricNames?.length ?? 0), 9), 0);
    }
}
